import { produce } from "immer";
import FrontendConstant from "../../constant/FrontendConstant";
import { useEffect } from "react";
import Multiselect from "../Multiselect";
import moment from "moment/moment";

const isGeneticMode = (algorithm) => algorithm === "GENETIC";

const Component = ({
  jobParam,
  ruleInfoItems,
  mandatoryIgnoredRuleScoreColumns,
  onChange,
}) => {
  useEffect(async () => {}, []);

  useEffect(async () => {}, [jobParam.trnDateMin, jobParam.trnDateMax]);

  const algorithm = jobParam.searchAlgorithm || "RANDOM";

  const handleAlgorithmChange = (newAlgorithm) => {
    onChange(
      produce(jobParam, (d) => {
        d.searchAlgorithm = newAlgorithm;
        d.foundWeightsMax = FrontendConstant.defaultFoundWeightsMax[newAlgorithm];
      }),
    );
  };

  return (
    <>
      <table className="config-table">
        <thead>
          <tr>
            <th>Attribute</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Trn date min.</td>
            <td>
              <input
                type="datetime-local"
                value={moment(jobParam.trnDateMin).format("YYYY-MM-DDT00:00:00")}
                max={jobParam.trnDateMax}
                onChange={async (e) => {
                  const trnDateMin =
                    e.target.value.replace(/T..:../, "T00:00") + "Z";
                  onChange(produce(jobParam, (d) => { d.trnDateMin = trnDateMin; }));
                }}
                disabled={!onChange}
              />
            </td>
          </tr>
          <tr>
            <td>Trn date max.</td>
            <td>
              <input
                type="datetime-local"
                value={moment(jobParam.trnDateMax).format("YYYY-MM-DDT00:00:00")}
                min={jobParam.trnDateMin}
                onChange={async (e) => {
                  const trnDateMax =
                    e.target.value.replace(/T..:../, "T00:00") + "Z";
                  onChange(produce(jobParam, (d) => { d.trnDateMax = trnDateMax; }));
                }}
                disabled={!onChange}
              />
            </td>
          </tr>
          <tr>
            <td>False positives max.</td>
            <td>
              <input
                type="number"
                value={jobParam.falsePositivesMax}
                min={0}
                onChange={(e) =>
                  onChange(produce(jobParam, (d) => { d.falsePositivesMax = e.target.value; }))
                }
                disabled={!onChange}
              />
            </td>
          </tr>
          <tr>
            <td>True positives min.</td>
            <td>
              <input
                type="number"
                value={jobParam.truePositivesMin}
                min={0}
                onChange={(e) =>
                  onChange(produce(jobParam, (d) => { d.truePositivesMin = e.target.value; }))
                }
                disabled={!onChange}
              />
            </td>
          </tr>
          <tr>
            <td>Run duration seconds max.</td>
            <td>
              <input
                type="number"
                value={jobParam.runDurationSecondsMax}
                onChange={(e) =>
                  onChange(
                    produce(jobParam, (d) => {
                      d.runDurationSecondsMax =
                        e.target.value > FrontendConstant.lambdaRunDurationSecondsMax
                          ? FrontendConstant.lambdaRunDurationSecondsMax
                          : e.target.value;
                    }),
                  )
                }
                disabled={!onChange}
              />
            </td>
          </tr>
          <tr>
            <td>Threshold</td>
            <td>
              <input
                type="number"
                value={jobParam.threshold}
                max={840}
                onChange={(e) =>
                  onChange(produce(jobParam, (d) => { d.threshold = e.target.value; }))
                }
                disabled={!onChange}
              />
            </td>
          </tr>
          <tr>
            <td>Weights found max.</td>
            <td>
              <input
                type="number"
                value={jobParam.foundWeightsMax}
                min={1}
                max={1000}
                onChange={(e) =>
                  onChange(
                    produce(jobParam, (d) => {
                      d.foundWeightsMax = e.target.value > 1000 ? 1000 : e.target.value;
                    }),
                  )
                }
                disabled={!onChange}
              />
            </td>
          </tr>
          <tr>
            <td>Search algorithm</td>
            <td>
              <select
                value={algorithm}
                onChange={(e) => handleAlgorithmChange(e.target.value)}
                disabled={!onChange}
              >
                {FrontendConstant.searchAlgorithms.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </td>
          </tr>
          {isGeneticMode(algorithm) && (
            <>
              <tr>
                <td>
                  GA population size
                  <span
                    className="info-hint"
                    data-hint="Number of candidate weight sets evolved per generation. Default: 200. Larger = more diversity, slower per generation."
                  >
                    ⓘ
                  </span>
                </td>
                <td>
                  <input
                    type="number"
                    value={jobParam.gaPopulationSize || 200}
                    min={10}
                    max={2000}
                    onChange={(e) =>
                      onChange(
                        produce(jobParam, (d) => {
                          d.gaPopulationSize = parseInt(e.target.value, 10);
                        }),
                      )
                    }
                    disabled={!onChange}
                  />
                </td>
              </tr>
              <tr>
                <td>
                  GA max generations
                  <span
                    className="info-hint"
                    data-hint="Hard cap on evolution generations. Leave empty to run until the time limit."
                  >
                    ⓘ
                  </span>
                </td>
                <td>
                  <input
                    type="number"
                    value={jobParam.gaMaxGenerations || ""}
                    placeholder="unlimited"
                    min={1}
                    onChange={(e) =>
                      onChange(
                        produce(jobParam, (d) => {
                          d.gaMaxGenerations = e.target.value
                            ? parseInt(e.target.value, 10)
                            : null;
                        }),
                      )
                    }
                    disabled={!onChange}
                  />
                </td>
              </tr>
            </>
          )}
          <tr>
            <td>Mandatory ignored rules for the selected Trn date range</td>
            <td>
              <Multiselect
                values={ruleInfoItems.map((rii) => rii.ruleScoreColumnName)}
                selectedValues={
                  mandatoryIgnoredRuleScoreColumns
                    ? mandatoryIgnoredRuleScoreColumns
                    : []
                }
                disabled={true}
              ></Multiselect>
            </td>
          </tr>
          <tr>
            <td>Ignored rules</td>
            <td>
              <Multiselect
                values={ruleInfoItems.map((rii) => rii.ruleScoreColumnName)}
                selectedValues={
                  jobParam.ignoredRuleScoreColumns
                    ? jobParam.ignoredRuleScoreColumns
                    : []
                }
                onChange={(e) =>
                  onChange(produce(jobParam, (d) => { d.ignoredRuleScoreColumns = e; }))
                }
              ></Multiselect>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export default Component;
